import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RagaLadder } from "@/components/RagaLadder";

export default function SouthIndian3() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="EQ783EHQkng"
        title="Moongil Thottam — A.R. Rahman · Kadal (2013)"
        caption="Count the waltz pulse as you listen: ONE-two-three, ONE-two-three. Notice when the song feels like sunlight — and when it shifts to shadow."
      />

      <SISCard label="The big idea" title="Two homes, one set of notes">
        <p>
          Moongil Thottam is built on one of music's most elegant illusions: a change of home key
          that happens without you noticing. The song floats between G major and E minor — and
          back — without any announcement that the tonal centre has shifted. You are standing in
          a different room before you realise you walked through a doorway.
        </p>
        <p>
          This works because G major and E minor are <em>relative keys</em>: they share a key
          signature and the same seven notes, G–A–B–C–D–E–F♯. The difference is which note feels
          like home — and the <strong>cadences</strong> decide that. D7 → G or C → G lands you in
          G major. B7 → Em or Am → Em lands you in E minor (the B7 borrows one extra note, D♯,
          the raised 7th of E minor, to pull harder). Rahman keeps choosing between them, and the
          emotional colour of the song changes with each choice.
        </p>
        <p>
          Jason's summary of what Rahman is doing: <em>"He's using the same seven odd chords,
          the same set, but all the different cadences."</em> Once you understand that, you will
          hear major–minor floating in a lot of film music.
        </p>
      </SISCard>

      <SISCard label="The waltz pulse" title="3/4 — one heavy, two light">
        <p>
          Moongil Thottam is in <strong>3/4 meter</strong> — the waltz, a <em>simple triple</em>
          meter (three beats, each dividing in two). Many songs in this course are in 4/4, so the
          feel is different. The pattern is: <strong>ONE</strong>-two-three,
          <strong> ONE</strong>-two-three. Beat one lands with weight; beats two and three are light.
        </p>
        <p>
          For pianists, this changes phrasing. In 4/4 you tend to think in two-bar units. In 3/4
          you think in three-beat units that group into phrases of six, nine, or twelve beats.
          Jason's tip for a play-through: it's just three-four, so an oom-pah-pah left hand —
          or any rhythm you like — will carry the chords.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Cadences that land in G major (from Jason's chart)"
        caption="ii–V7–I (Am → D7 → G) and IV–I (C → G) both say 'home is G'. The last group is the deceptive cadence: C → D sets up G, but D goes to Em instead — the moment the song leans toward its shadow."
        chords={[
          { chord: "Am", rn: "ii",  function: "Pre-dominant", pitches: ["A3", "C4", "E4"] },
          { chord: "D7", rn: "V7",  function: "Dominant",     pitches: ["D3", "F#3", "A3", "C4"] },
          { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3", "B3", "D4"] },
          { bar: true },
          { chord: "C",  rn: "IV",  function: "Pre-dominant", pitches: ["C3", "E3", "G3"] },
          { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3", "B3", "D4"] },
          { bar: true },
          { chord: "C",  rn: "IV",  function: "Pre-dominant", pitches: ["C3", "E3", "G3"] },
          { chord: "D",  rn: "V",   function: "Dominant",     pitches: ["D3", "F#3", "A3"] },
          { chord: "Em", rn: "vi",  function: "Submediant",   pitches: ["E3", "G3", "B3"] },
        ]}
      />

      <RomanNumeralAnalysis
        label="Cadences that land in E minor (from Jason's chart)"
        caption="Now E minor is home. Am → Em is the minor plagal cadence (iv–i). B7 → Em is V7–i — the B7 brings in D♯, the raised 7th, which pulls up to E. F♯m7♭5 → B7 → Em is the minor ii–V–i."
        chords={[
          { chord: "Am",     rn: "iv",   function: "Pre-dominant", pitches: ["A3", "C4", "E4"] },
          { chord: "Em",     rn: "i",    function: "Tonic",        pitches: ["E3", "G3", "B3", "E4"] },
          { bar: true },
          { chord: "B7",     rn: "V7",   function: "Dominant",     pitches: ["B2", "D#3", "F#3", "A3"] },
          { chord: "Em",     rn: "i",    function: "Tonic",        pitches: ["E3", "G3", "B3", "E4"] },
          { bar: true },
          { chord: "F♯m7♭5", rn: "iiø7", function: "Pre-dominant", pitches: ["F#3", "A3", "C4", "E4"] },
          { chord: "B7",     rn: "V7",   function: "Dominant",     pitches: ["B2", "D#3", "F#3", "A3"] },
          { chord: "Em",     rn: "i",    function: "Tonic",        pitches: ["E3", "G3", "B3", "E4"] },
        ]}
      />

      <SISCard label="The mechanics of the modulation" title="How Rahman moves between the two keys">
        <p>
          The pivot is deceptively simple. In G major, the chord Em (E minor) is the sixth chord —
          the vi. In E minor, the chord Em is the first chord — the i. They are the same chord. So
          when Rahman wants to move from G major to E minor, he simply <strong>lets Em land with
          more weight</strong> — holds it longer, lets the melody settle on E, makes it feel like
          arrival rather than passing through.
        </p>
        <p>
          The deceptive cadence (V → vi in G major: D major resolving to E minor instead of G major)
          is the technical mechanism. We expect the D major chord to resolve "home" to G. Instead
          it resolves to Em — the relative minor. This is called <strong>deceptive</strong> because
          the ear is fooled. But here, it is not a trick — it is a doorway. D → Em is both the
          end of the G major section and the beginning of the E minor section simultaneously.
        </p>
        <p>
          When Rahman wants E minor to sound fully at home, he reaches for <strong>B7</strong>
          (often after a B suspended chord) rather than a plain Bm — and B7 is the one chord here
          that needs a note outside G major, the D♯. Jason's advice: if you hear a B7, you are
          probably in E minor.
        </p>
      </SISCard>

      <RagaLadder
        title="Shankarabharanam / Natabhairavi — Sa = G or E"
        tonic="G"
        steps={[
          { swara: "Sa",  pitch: "G3",  notes: "G major: home",         flat: false },
          { swara: "Ri₂", pitch: "A3",  notes: "Major 2nd",             flat: false },
          { swara: "Ga₃", pitch: "B3",  notes: "Major 3rd — brightness", flat: false },
          { swara: "Ma₁", pitch: "C4",  notes: "Perfect 4th",           flat: false },
          { swara: "Pa",  pitch: "D4",  notes: "Perfect 5th",           flat: false },
          { swara: "Dha₂",pitch: "E4",  notes: "Major 6th / Em: home",  flat: false },
          { swara: "Ni₃", pitch: "F#4", notes: "Major 7th",             flat: false },
          { swara: "Sa",  pitch: "G4",  notes: "Octave",                flat: false },
        ]}
      />

      <SISCard label="Carnatic connection" title="Shankarabharanam and Natabhairavi — two moods, one scale">
        <p>
          In Carnatic theory, the G major scale is <strong>Shankarabharanam</strong> (29th
          Melakarta — the Carnatic equivalent of the Western major scale). Rooted on E, the same
          notes produce <strong>Natabhairavi</strong> (20th Melakarta — the natural minor).
          Same seven notes, two different homes.
        </p>
        <p>
          The B7 cadence adds one twist: its D♯ is the raised 7th of E minor, so at those moments
          the minor side borrows from <strong>Kiravani</strong> (21st Melakarta — the harmonic
          minor). Rahman builds both emotional worlds from almost the same material, mainly by
          shifting which note is treated as home.
        </p>
        <p>
          This is one of the most profound concepts in both Western and Carnatic music: <em>the
          same notes sound different depending on which one you emphasise as tonic</em>. The scale
          is not the mood. The tonic is the mood.
        </p>
      </SISCard>

      <SISCallout label="Practise this — hear the shift before you play it">
        Sit at your piano. Play G major: G–A–B–C–D–E–F♯. Now play the same notes but start and
        end on E: E–F♯–G–A–B–C–D–E. Do you hear the difference? The first sounds like arriving
        home. The second sounds like searching for home. Now improvise a simple melody over a G
        major chord for 30 seconds. Then over an E minor chord for 30 seconds — same notes, same
        fingers. The shift you feel is exactly what Rahman does in Moongil Thottam.
      </SISCallout>

      <SISInfluence artists={["A.R. Rahman — Kadal (2013)", "12-string guitar"]}>
        <p>
          Jason's main takeaway from this song is how easy it makes the chord changes to hear:
          Rahman uses the same small set of chords throughout, but cycles through nearly every
          cadence between them. Listen also to the guitar texture — Jason identifies it as a
          12-string guitar, where each string is doubled by a second string (an octave above on
          the lower strings), which is why the notes ring so clearly.
        </p>
      </SISInfluence>

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/03-moongil-thottam-page1.png"
        caption="Jason's chart — G major ⇄ E minor in 3/4, the cadences that land on each (D7 → G, C → G, Am → D7 → G; B7 → Em, Am → Em, F♯m7♭5 → B7 → Em; the deceptive D → Em), and the seven chords of G major with G and Em circled."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/03-moongil-thottam-page2.png"
        caption="Page 2 — the chord chart for the song's sections, with B7 → Em and Bsus4 → B appearing throughout."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/03-moongil-thottam-page3.png"
        caption="Page 3 — the rest of the chart, including the duet verse and a section that travels further afield (A/G, F♯sus4 → F♯7 → Bm, and a B♭ over D)."
      />

      <SISTakeaway>
        Major and minor are not separate musical worlds — they are the same world seen from
        two different vantage points. G major and E minor share a key signature. Learning to
        move between relative keys — to shift the tonic with cadences rather than with new
        notes — gives you access to an enormous emotional range with minimal harmonic
        complexity. This is not a shortcut. It is one of the most sophisticated tools in tonal
        music.
      </SISTakeaway>
    </LessonShell>
  );
}
